import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

export const IMPLIED_ODDS_TOOLTIP =
  'Implied odds calculated by the hit-rate of all enabled counts. Deadheat rules and ties apply. This should be used as a guide only.';

const TOOLTIP_WIDTH = 240;
const GAP = 8;
const VIEWPORT_PAD = 8;

// Small info icon + tooltip for "Implied Odds" column headers.
//
// The tooltip is rendered through a portal into <body> and positioned with
// fixed coordinates: table headers live inside overflow-x-auto scrollers and
// sticky containers that would otherwise clip an absolutely positioned bubble.
// Opens on hover/focus for pointer users and on tap for touch users.
const ImpliedOddsInfo = ({ className = '' }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // { left, top, placement, arrowLeft }
  const buttonRef = useRef(null);
  const tooltipRef = useRef(null);

  // Compute placement from the icon's screen rect. Prefer above; flip below
  // when there isn't room. Clamp horizontally so it never leaves the viewport
  // and slide the arrow so it keeps pointing at the icon.
  const place = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const tipH = tooltipRef.current?.offsetHeight || 0;
    const vw = window.innerWidth;
    const width = Math.min(TOOLTIP_WIDTH, vw - VIEWPORT_PAD * 2);
    const centerX = r.left + r.width / 2;
    let left = centerX - width / 2;
    left = Math.max(VIEWPORT_PAD, Math.min(left, vw - width - VIEWPORT_PAD));
    const fitsAbove = r.top - GAP - tipH >= VIEWPORT_PAD;
    const placement = fitsAbove ? 'top' : 'bottom';
    const top = fitsAbove ? r.top - GAP - tipH : r.bottom + GAP;
    setPos({ left, top, width, placement, arrowLeft: centerX - left });
  };

  useLayoutEffect(() => {
    if (open) place();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onDocDown = (e) => {
      if (buttonRef.current && !buttonRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('touchstart', onDocDown);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('touchstart', onDocDown);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label="About implied odds"
        aria-describedby={open ? 'implied-odds-tooltip' : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={`inline-flex items-center justify-center align-middle ml-1 -my-0.5 p-0.5 rounded
          text-slate-500 hover:text-emerald-300 focus:text-emerald-300 focus:outline-none
          focus-visible:ring-1 focus-visible:ring-emerald-500/50 transition-colors ${className}`}
      >
        <Info className="w-3 h-3" strokeWidth={2} aria-hidden="true" />
      </button>

      {open &&
        createPortal(
          <span
            ref={tooltipRef}
            id="implied-odds-tooltip"
            role="tooltip"
            style={{
              position: 'fixed',
              left: pos?.left ?? 0,
              top: pos?.top ?? 0,
              width: pos?.width ?? TOOLTIP_WIDTH,
              visibility: pos ? 'visible' : 'hidden',
              zIndex: 10000,
            }}
            className="pointer-events-none block px-3 py-2 rounded-md text-[11px] leading-snug font-sans font-normal normal-case tracking-normal text-left text-slate-200 bg-slate-800/95 backdrop-blur border border-slate-700/60 shadow-xl shadow-black/40"
          >
            {IMPLIED_ODDS_TOOLTIP}
            {/* Arrow: border layer + fill layer, pointing at the icon */}
            {pos && (
              <>
                <span
                  aria-hidden="true"
                  style={{ left: pos.arrowLeft }}
                  className={`absolute -translate-x-1/2 w-0 h-0 border-x-[5px] border-x-transparent ${
                    pos.placement === 'top'
                      ? 'top-full border-t-[5px] border-t-slate-700/60'
                      : 'bottom-full border-b-[5px] border-b-slate-700/60'
                  }`}
                />
                <span
                  aria-hidden="true"
                  style={{ left: pos.arrowLeft }}
                  className={`absolute -translate-x-1/2 w-0 h-0 border-x-[5px] border-x-transparent ${
                    pos.placement === 'top'
                      ? 'top-[calc(100%-1px)] border-t-[5px] border-t-slate-800/95'
                      : 'bottom-[calc(100%-1px)] border-b-[5px] border-b-slate-800/95'
                  }`}
                />
              </>
            )}
          </span>,
          document.body
        )}
    </>
  );
};

export default ImpliedOddsInfo;
